import { useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import InstallmentModal from "../components/InstallmentModal";
import OtherFeeModal from "../components/OtherFeeModal";

const inputCls =
  "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/30 transition";

const selectCls =
  "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-3 text-sm text-white appearance-none focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/30 transition cursor-pointer";

const labelCls =
  "block text-xs font-medium uppercase tracking-widest text-slate-400 mb-1.5";

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>
      {label}
      {required && <span className="text-pink-500 ml-1">*</span>}
    </label>
    <div className="relative">
      {children}
    </div>
  </div>
);

const Step2Financial = ({ form, setForm, onNext, onBack }) => {
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [showOtherFeeModal, setShowOtherFeeModal] = useState(false);
  const [error, setError] = useState("");

  const addInstallment = (data) => {
    setForm({ ...form, installments: [...(form.installments || []), data] });
  };

  const addOtherFee = (data) => {
    setForm({ ...form, other_fees: [...(form.other_fees || []), data] });
  };

  const removeInstallment = (index) => {
    const updated = [...form.installments];
    updated.splice(index, 1);
    setForm({ ...form, installments: updated });
  };

  const removeOtherFee = (index) => {
    const updated = [...form.other_fees];
    updated.splice(index, 1);
    setForm({ ...form, other_fees: updated });
  };

  const validate = () => {
    if (!form.currency || !form.artist_fee || !form.booking_fee) {
      setError("* Marked fields are required.");
      return false;
    }
    setError("");
    return true;
  };

  const handleNext = () => {
    if (!validate()) return;
    onNext();
  };

  // Derived summary
  const artistFee = parseFloat(form.artist_fee) || 0;
  const bookingFee = parseFloat(form.booking_fee) || 0;
  const otherTotal = (form.other_fees || []).reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);
  const grandTotal = artistFee + bookingFee + otherTotal;
  const symbol = form.currency || "—";

  return (
    <div className="space-y-8">

      {/* ── Step header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pink-500 bg-pink-500/10 border border-pink-500/20 px-2.5 py-0.5 rounded-full">
              Step 2 of 3
            </span>
          </div>
          <h2 className="text-xl font-semibold text-white">Financial Information</h2>
          <p className="text-sm text-slate-500 mt-0.5">Set fees, currency, and any additional charges</p>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT — Core financials */}
        <div className="bg-slate-900/60 rounded-2xl p-6 space-y-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium pb-1">Core Fees</p>

          {/* Currency */}
          <Field label="Currency" required>
            <select
              value={form.currency || ""}
              onChange={(e) => { setForm({ ...form, currency: e.target.value }); setError(""); }}
              className={selectCls}
            >
              <option value="">Select currency…</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
              <option value="AED">AED</option>
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </Field>

          {/* Artist Fee + Booking Fee side by side */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Artist Fee" required>
              <input
                type="number"
                value={form.artist_fee || ""}
                onChange={(e) => { setForm({ ...form, artist_fee: e.target.value }); setError(""); }}
                placeholder="0.00"
                className={inputCls}
              />
            </Field>
            <Field label="Booking Fee" required>
              <input
                type="number"
                value={form.booking_fee || ""}
                onChange={(e) => { setForm({ ...form, booking_fee: e.target.value }); setError(""); }}
                placeholder="0.00"
                className={inputCls}
              />
            </Field>
          </div>
        </div>

        {/* RIGHT — Other fees */}
        <div className="bg-slate-900/60 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">Other Fees</p>
              <p className="text-[11px] text-slate-600 mt-0.5">Additional charges beyond core fees</p>
            </div>
            <button
              onClick={() => setShowOtherFeeModal(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-pink-400 hover:text-pink-300 bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20 px-3 py-1.5 rounded-lg transition"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add Fee
            </button>
          </div>

          {/* Other fees table */}
          {(form.other_fees || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center mb-3">
                <PlusIcon className="w-5 h-5 text-slate-600" />
              </div>
              <p className="text-sm text-slate-500">No additional fees yet</p>
              <p className="text-xs text-slate-600 mt-0.5">Click "Add Fee" to include extras</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-gray-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5 text-left">Description</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-right w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.other_fees.map((fee, i) => (
                    <tr key={i} className="border-t border-white/5 hover:bg-slate-800/30 transition">
                      <td className="px-4 py-3 text-slate-300">{fee.description}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">
                        {symbol} {parseFloat(fee.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => removeOtherFee(i)}
                          className="p-1 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex flex-col items-center gap-3 pt-2">
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <button
            onClick={handleNext}
            className="group flex items-center gap-2.5 bg-pink-600 hover:bg-pink-700 active:bg-pink-800 px-10 py-3 rounded-xl text-white font-medium text-sm transition-all shadow-lg shadow-pink-900/30"
          >
            Continue to Next Step
            <svg
              className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Modals ── */}
      {showInstallmentModal && (
        <InstallmentModal
          onClose={() => setShowInstallmentModal(false)}
          onSave={addInstallment}
        />
      )}

      {showOtherFeeModal && (
        <OtherFeeModal
          onClose={() => setShowOtherFeeModal(false)}
          onSave={addOtherFee}
        />
      )}
    </div>
  );
};

export default Step2Financial;